import type { Submission } from "@/api/submissions.service";
import type { Referee, RefereeWithTranslations } from "@/api/referees.service";
import type {
  TranslatableEntity,
  AddTranslations,
  SubmissionWithTranslations,
} from "@/hooks/useTranslations";
import { translationsService, getLanguageCodeForRegion } from "@/api/translations.service";

export async function batchTranslate<T extends TranslatableEntity>(
  entities: T[],
  getEntityId: (entity: T) => string | undefined,
  onProgress?: (current: number, total: number) => void,
): Promise<AddTranslations<T>[]> {
  const entitiesByLanguage = new Map<string, T[]>();
  const entitiesWithoutTranslations: T[] = [];

  entities.forEach(entity => {
    const languageCode = getLanguageCodeForRegion(entity.regionName ?? undefined);

    if (!languageCode) {
      entitiesWithoutTranslations.push(entity);
      return;
    }

    // Initialize array if this is first entity for this language
    if (!entitiesByLanguage.has(languageCode)) {
      entitiesByLanguage.set(languageCode, []);
    }

    entitiesByLanguage.get(languageCode)!.push(entity);
  });

  const languages = Array.from(entitiesByLanguage.keys());
  const translatedGroups: AddTranslations<T>[] = [];

  for (let i = 0; i < languages.length; i++) {
    const languageCode = languages[i];
    const groupEntities = entitiesByLanguage.get(languageCode)!;

    const termIds = new Set<string>();

    groupEntities.forEach(entity => {
      if (entity.regionId) termIds.add(String(entity.regionId));
      if (entity.practiceGroupId) termIds.add(String(entity.practiceGroupId));
      if (entity.practiceAreaId) termIds.add(String(entity.practiceAreaId));
      if (entity.tableId) termIds.add(String(entity.tableId));
    });

    const translationMap = await translationsService.batchQuery(languageCode, Array.from(termIds));

    const translated = groupEntities.map(
      (entity): AddTranslations<T> => ({
        ...entity,
        languageCode,
        regionTranslated: entity.regionId
          ? translationMap.get(String(entity.regionId)) || undefined
          : undefined,
        practiceGroupTranslated: entity.practiceGroupId
          ? translationMap.get(String(entity.practiceGroupId)) || undefined
          : undefined,
        practiceAreaTranslated: entity.practiceAreaId
          ? translationMap.get(String(entity.practiceAreaId)) || undefined
          : undefined,
        tableTranslated: entity.tableId
          ? translationMap.get(String(entity.tableId)) || undefined
          : undefined,
      }),
    );

    translatedGroups.push(...translated);

    if (onProgress) {
      onProgress(i + 1, languages.length);
    }
  }

  const resultMap = new Map<string, AddTranslations<T>>();

  translatedGroups.forEach(translated => {
    const entityId = getEntityId(translated);
    if (entityId) {
      resultMap.set(entityId, translated);
    }
  });

  entitiesWithoutTranslations.forEach(entity => {
    const entityId = getEntityId(entity);
    if (entityId) {
      resultMap.set(entityId, entity);
    }
  });

  return entities.map(entity => {
    const entityId = getEntityId(entity);
    return entityId ? resultMap.get(entityId) || entity : entity;
  });
}

export async function batchTranslateSubmissions(
  submissions: Submission[],
  onProgress?: (current: number, total: number) => void,
): Promise<SubmissionWithTranslations[]> {
  return batchTranslate(submissions, s => s.submissionId, onProgress);
}

export async function batchTranslateReferees(
  referees: Referee[],
  onProgress?: (current: number, total: number) => void,
): Promise<RefereeWithTranslations[]> {
  return batchTranslate(referees, r => r.id, onProgress);
}
